import { handleActions, createAction } from "redux-actions";

import { axios } from "../cores/axiosWrapper";
import { nanoid } from "nanoid";
import configurationTemplate from "./event-trigger-configuration-template";
import configurationTemplateV2 from "./event-trigger-configuration-template-v2";
import replaceCustomTags from "../cores/replaceCustomTags";
import buildAutoCaptionOptions from "@utils/build-auto-caption-options";
import validateInput from "@cores/validateInput";

function createEventTriggerAPI(stageId, apiEndpoint, projectId, data) {
    return axios.post(`${apiEndpoint}/triggers`, data, {
        headers: {
            stageId,
            projectId,
        },
    });
}

function correctSelectedAction(fieldAreaUniqKey, action, validationErrors = {}) {
    action.customFields = action.customFields.map((v) => {
        if (v.required && !v.defaultValue) {
            validationErrors[`actions-${fieldAreaUniqKey}-${v.key}`] = validateInput(null, { required: true });
        }
        return { ...v, uniqKey: nanoid() };
    });

    return action;
}

function getStateByConfigurationTemplate(template, validationErrors = {}) {
    Object.keys(validationErrors).forEach((v) => {
        if (v.indexOf("conditions") > -1 || v.indexOf("actions") > -1) {
            delete validationErrors[v];
        }
    });

    const conditionCustomFields = template.conditionCustomFields.map((v) => {
        v.uniqKey = nanoid();
        v.isFolded = !v.required;
        v.value = "";

        if (v.required) {
            validationErrors[`conditions-${v.uniqKey}`] = validateInput(null, { required: true });
        }

        return v;
    });

    return {
        data: {
            eventType: template.eventType,
            conditions: conditionCustomFields.map((v) => {
                return {
                    key: v.key,
                    uniqKey: v.uniqKey,
                };
            }),
            actions: [],
        },
        validationErrors,
        selectedConfiguration: template,
        conditionCustomFields,
        actionCustomFieldAreas: [],
    };
}

export function correctPayload(payload) {
    if (!payload) return payload;

    Object.keys(payload).forEach((k) => {
        if (k.indexOf("-") > -1) {
            const keys = k.split("-");
            const values = payload[k] && payload[k].split("-");

            keys.forEach((_k, i) => {
                payload[_k] = values[i];
            });
        }
    });

    if (payload.autoCaptions?.useAutoCaption === "USED") {
        const captionData = payload.autoCaptions.data;

        payload.autoCaptions = captionData.services?.map((service) => ({
            attributions: [
                {
                    // Note: 이 값은 고정(백엔드에서 사용)
                    key: "SOURCE_ASSET_ID",
                    value: "{{:sourceAssetId:}}",
                },
            ],
            engine: service.serviceType,
            options: buildAutoCaptionOptions(service.serviceType, captionData) ?? {},
        }));
    } else {
        delete payload.autoCaptions;
    }

    if (payload.categories?.length > 0) {
        payload.categories.forEach((category) => delete category.categoryGroup?.name);
    }

    if (payload.virtualFolder) {
        if (payload.virtualFolder.isAutoVirtualFolderEnabled) {
            payload.isAutoVirtualFolderEnabled = payload.virtualFolder.isAutoVirtualFolderEnabled;
            delete payload.virtualFolder;
        } else {
            delete payload.virtualFolder.isAutoVirtualFolderEnabled;
            delete payload.virtualFolder.virtualFolderPath;
            if (!payload.virtualFolder?.id || !payload.virtualFolder?.virtualDrive?.id) delete payload.virtualFolder;
        }
    }

    if (payload.attributions) {
        payload.attributions.forEach((attr) => delete attr.id);
    }

    // aiAnalysis에서 prompt preset 항목을 분리하여 AI 프로퍼티로 이동
    if (payload.aiAnalysis && Array.isArray(payload.aiAnalysis)) {
        const regularItems = [];
        const presetItems = [];

        payload.aiAnalysis.forEach((item) => {
            if (item.promptPresetId) {
                presetItems.push({
                    engineId: item.engineId,
                    promptPresetId: item.promptPresetId,
                });
            } else {
                regularItems.push(item);
            }
        });

        payload.aiAnalysis = regularItems;

        if (presetItems.length > 0) {
            payload.AI = presetItems;
        }
    }

    return payload;
}

const CREATE_EVENT_TRIGGER_PENDING = "CREATE_EVENT_TRIGGER_PENDING";
const CREATE_EVENT_TRIGGER_FAILURE = "CREATE_EVENT_TRIGGER_FAILURE";

const CREATE_EVENT_TRIGGER_UPDATE_DATA = "CREATE_EVENT_TRIGGER_UPDATE_DATA";

const CREATE_EVENT_TRIGGER_UPDATE_CONDITION_CUSTOM_FIELD = "CREATE_EVENT_TRIGGER_UPDATE_CONDITION_CUSTOM_FIELD";
const CREATE_EVENT_TRIGGER_TOGGLE_CONDITION_CUSTOM_FIELD_EXPANDER =
    "CREATE_EVENT_TRIGGER_TOGGLE_CONDITION_CUSTOM_FIELD_EXPANDER";

const CREATE_EVENT_TRIGGER_RESET = "CREATE_EVENT_TRIGGER_RESET";

const CREATE_EVENT_TRIGGER_SET_VALIDATION_ERROR = "CREATE_EVENT_TRIGGER_SET_VALIDATION_ERROR";

const CREATE_EVENT_TRIGGER_SET_CONFIGURATION_TEMPLATE = "CREATE_EVENT_TRIGGER_SET_CONFIGURATION_TEMPLATE";

const CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD_AREA = "CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD_AREA";
const CREATE_EVENT_TRIGGER_ADD_ACTION_CUSTOM_FIELD_AREA = "CREATE_EVENT_TRIGGER_ADD_ACTION_CUSTOM_FIELD_AREA";
const CREATE_EVENT_TRIGGER_REMOVE_ACTION_CUSTOM_FIELD_AREA = "CREATE_EVENT_TRIGGER_REMOVE_ACTION_CUSTOM_FIELD_AREA";
const CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD = "CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD";
const CREATE_EVENT_TRIGGER_TOGGLE_ACTION_CUSTOM_FIELD_EXPANDER =
    "CREATE_EVENT_TRIGGER_TOGGLE_ACTION_CUSTOM_FIELD_EXPANDER";

const initialState = {
    pending: false,
    error: false,
    version: null,
    ...getStateByConfigurationTemplate(configurationTemplate[0]),
};

export default handleActions(
    {
        [CREATE_EVENT_TRIGGER_PENDING]: (state, action) => {
            return {
                ...state,
                pending: true,
            };
        },
        [CREATE_EVENT_TRIGGER_FAILURE]: (state, action) => {
            return {
                ...state,
                error: true,
            };
        },
        [CREATE_EVENT_TRIGGER_SET_VALIDATION_ERROR]: (state, action) => {
            const { key, error } = action.payload;

            if (error) {
                return {
                    ...state,
                    validationErrors: {
                        ...state.validationErrors,
                        [key]: error,
                    },
                };
            }

            const validationErrors = state.validationErrors;
            if (validationErrors[key]) {
                delete validationErrors[key];
            }

            return {
                ...state,
                validationErrors,
            };
        },
        [CREATE_EVENT_TRIGGER_UPDATE_DATA]: (state, action) => {
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.payload,
                },
            };
        },
        [CREATE_EVENT_TRIGGER_RESET]: (state, action) => {
            const { stage } = action.payload;
            return {
                pending: false,
                error: false,
                ...getStateByConfigurationTemplate(configurationTemplateV2[0]),
                stage,
            };
        },
        [CREATE_EVENT_TRIGGER_UPDATE_CONDITION_CUSTOM_FIELD]: (state, action) => {
            const { uniqKey, value } = action.payload;

            return {
                ...state,
                conditionCustomFields: state.conditionCustomFields.map((v) => {
                    if (v.uniqKey === uniqKey) {
                        v.value = value;
                    }

                    return v;
                }),
                data: {
                    ...state.data,
                    conditions: state.data.conditions.map((v, i) => {
                        if (v.uniqKey === uniqKey) {
                            v.value = value;
                        }

                        return v;
                    }),
                },
            };
        },
        [CREATE_EVENT_TRIGGER_TOGGLE_CONDITION_CUSTOM_FIELD_EXPANDER]: (state, action) => {
            const { uniqKey } = action.payload;

            return {
                ...state,
                conditionCustomFields: state.conditionCustomFields.map((c) => {
                    if (c.uniqKey === uniqKey) {
                        c.isFolded = !c.isFolded;
                    }

                    return c;
                }),
            };
        },
        [CREATE_EVENT_TRIGGER_SET_CONFIGURATION_TEMPLATE]: (state, action) => {
            const { eventType } = action.payload;
            const template = configurationTemplateV2[0];

            const selectedConfigurationTemplate = template
                .filter((v) => v.eventType === eventType)
                .reduce((acc, curr) => curr, {});

            return {
                ...state,
                ...getStateByConfigurationTemplate(selectedConfigurationTemplate, state.validationErrors),
            };
        },
        [CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD_AREA]: (state, action) => {
            const { uniqKey, index } = action.payload;

            const validationErrors = state.validationErrors;
            Object.keys(validationErrors).forEach((k) => {
                if (k.indexOf(`actions-${uniqKey}`) > -1) {
                    delete validationErrors[k];
                }
            });

            const selectedAction = correctSelectedAction(
                uniqKey,
                state.selectedConfiguration.actions.filter((v) => v.index === index)[0],
                validationErrors,
            );
            return {
                ...state,
                actionCustomFieldAreas: state.actionCustomFieldAreas.map((v) => {
                    if (v.uniqKey === uniqKey) {
                        v.selectedAction = selectedAction;
                    }

                    return v;
                }),
                data: {
                    ...state.data,
                    key: selectedAction.key,
                    actions: state.data.actions.map((a, i) => {
                        if (a.uniqKey === uniqKey) {
                            return {
                                uniqKey,
                                index: selectedAction.index,
                                key: selectedAction.key,
                                payload: selectedAction.customFields.reduce((acc, curr) => {
                                    if (curr.defaultValue) {
                                        acc[curr.key] = curr.defaultValue;
                                    }

                                    return acc;
                                }, {}),
                            };
                        }

                        return a;
                    }),
                },
                validationErrors,
            };
        },
        [CREATE_EVENT_TRIGGER_TOGGLE_ACTION_CUSTOM_FIELD_EXPANDER]: (state, action) => {
            const { fieldAreaUniqKey, key } = action.payload;

            return {
                ...state,
                actionCustomFieldAreas: state.actionCustomFieldAreas.map((v) => {
                    if (v.uniqKey === fieldAreaUniqKey) {
                        v.selectedAction.customFields = v.selectedAction.customFields.map((c) => {
                            if (c.key === key) {
                                c.isFolded = !c.isFolded;
                            }

                            return c;
                        });
                    }

                    return v;
                }),
            };
        },
        [CREATE_EVENT_TRIGGER_ADD_ACTION_CUSTOM_FIELD_AREA]: (state, action) => {
            const { stage, hideContents } = action.payload;

            const actions = state.selectedConfiguration.actions;
            const filteredActions = actions.filter((a) => {
                if (hideContents) {
                    return a.key !== "/stage/jobs/start-transcoding";
                }
                return true;
            });
            const selectedAction = filteredActions[0];

            const selectedActionCustomFieldArea = {
                uniqKey: nanoid(),
                selectedAction: selectedAction,
                options: filteredActions,
            };

            const validationErrors = state.validationErrors;
            selectedActionCustomFieldArea.selectedAction = correctSelectedAction(
                selectedActionCustomFieldArea.uniqKey,
                selectedAction,
                validationErrors,
            );
            const actionCustomFieldAreas = state.actionCustomFieldAreas.concat([selectedActionCustomFieldArea]);
            return {
                ...state,
                data: {
                    ...state.data,
                    actions: state.data.actions.concat([
                        {
                            uniqKey: selectedActionCustomFieldArea.uniqKey,
                            index: selectedActionCustomFieldArea.selectedAction.index,
                            key: selectedActionCustomFieldArea.selectedAction.key,
                            payload: selectedActionCustomFieldArea.selectedAction.customFields.reduce((acc, curr) => {
                                if (curr.defaultValue) {
                                    acc[curr.key] = curr.defaultValue;
                                }

                                return acc;
                            }, {}),
                        },
                    ]),
                },
                actionCustomFieldAreas,
                validationErrors,
            };
        },
        [CREATE_EVENT_TRIGGER_REMOVE_ACTION_CUSTOM_FIELD_AREA]: (state, action) => {
            const { fieldAreaUniqKey } = action.payload;

            const validationErrors = state.validationErrors;
            Object.keys(validationErrors).forEach((v) => {
                if (v.indexOf(`actions-${fieldAreaUniqKey}`) > -1) {
                    delete validationErrors[v];
                }
            });

            return {
                ...state,
                data: {
                    ...state.data,
                    actions: state.data.actions.filter((v) => v.uniqKey !== fieldAreaUniqKey),
                },
                actionCustomFieldAreas: state.actionCustomFieldAreas.filter((v) => v.uniqKey !== fieldAreaUniqKey),
                validationErrors,
            };
        },
        [CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD]: (state, action) => {
            const { fieldAreaUniqKey, key, value } = action.payload;

            return {
                ...state,
                data: {
                    ...state.data,
                    actions: state.data.actions.map((v, i) => {
                        if (v.uniqKey === fieldAreaUniqKey) {
                            v.payload[key] = value;
                        }

                        return v;
                    }),
                },
            };
        },
    },
    initialState,
);

export const reset = createAction(CREATE_EVENT_TRIGGER_RESET);
export const setValidationError = createAction(CREATE_EVENT_TRIGGER_SET_VALIDATION_ERROR);
export const updateData = createAction(CREATE_EVENT_TRIGGER_UPDATE_DATA);
export const updateConditionCustomField = createAction(CREATE_EVENT_TRIGGER_UPDATE_CONDITION_CUSTOM_FIELD);
export const toggleConditionCustomFieldExpander = createAction(
    CREATE_EVENT_TRIGGER_TOGGLE_CONDITION_CUSTOM_FIELD_EXPANDER,
);
export const setConfigurationTemplate = (eventType) => (dispatch) => {
    dispatch({
        type: CREATE_EVENT_TRIGGER_SET_CONFIGURATION_TEMPLATE,
        payload: { eventType },
    });
};
export const updateActionCustomFieldArea =
    ({ uniqKey, index }) =>
    (dispatch) => {
        dispatch({
            type: CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD_AREA,
            payload: { uniqKey, index },
        });
    };
export const toggleActionCustomFieldExpander = createAction(CREATE_EVENT_TRIGGER_TOGGLE_ACTION_CUSTOM_FIELD_EXPANDER);
export const removeActionCustomFieldArea = createAction(CREATE_EVENT_TRIGGER_REMOVE_ACTION_CUSTOM_FIELD_AREA);
export const updateActionCustomField = createAction(CREATE_EVENT_TRIGGER_UPDATE_ACTION_CUSTOM_FIELD);

export const addActionCustomFieldArea = () => (dispatch, getState) => {
    const { stage } = getState();
    const hideContents = stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";
    dispatch({
        type: CREATE_EVENT_TRIGGER_ADD_ACTION_CUSTOM_FIELD_AREA,
        payload: { stage, hideContents },
    });
};

export const createEventTrigger = (projectId, data) => async (dispatch, getState) => {
    const { stage, createEventTrigger } = getState();

    dispatch({ type: CREATE_EVENT_TRIGGER_PENDING });

    return new Promise((resolve, reject) => {
        const parsedData = JSON.parse(JSON.stringify(data));

        parsedData.actions = parsedData.actions.map((a) => {
            const matchedAction = createEventTrigger.selectedConfiguration.actions
                .filter((sa) => sa.index === a.index)
                .reduce((acc, curr) => curr, {});
            const payload = correctPayload(a.payload);

            return {
                ...a,
                payload: {
                    ...payload,
                    ...replaceCustomTags(matchedAction.automaticValues, payload),
                },
            };
        });

        createEventTriggerAPI(stage.id, stage.endpoint, projectId, parsedData)
            .then((response) => {
                resolve(response);
            })
            .catch((error) => {
                dispatch({
                    type: CREATE_EVENT_TRIGGER_FAILURE,
                    payload: error,
                });

                reject(error);
            });
    });
};
